import { useCallback, useEffect, useMemo, useState } from "react";

import { notifyUserEventInfo } from "../../side-effect/notify-user-event-info";

type Result = {
	idForPrintBlockEl: string;
	onGenerateIdForPrintSectionEl: (props: { prefix: string }) => string;
	onPrint: () => void;
};

/**
 * @example
 * root component
 * ```
 * const { idForPrintBlockEl } = useHandlePrint();
 *
 * return (
 *   <>
 *     // 印刷対象外の element
 *     <div className="print:hidden">
 *       // ここに印刷対象の component が入ることを想定
 *       // （ここに入れると print:hidden のため印刷時に表示されないので、印刷対象の element へ child として append する設計をしている。）
 *     </div>
 *
 *     // 印刷対象の element
 *     <div id={idForPrintBlockEl} className="print:block" />
 *   </>
 * );
 * ```
 *
 * @example
 * child component
 * ```
 * const { onGenerateIdForPrintSectionEl, onPrint } = useHandlePrint();
 *
 * // 印刷対象の element id を生成
 * const idForPrintSectionEl = useMemo(() => onGenerateIdForPrintSectionEl({ prefix: "management-activities-options" }), []);
 * // 印刷処理の実行
 * const handleClickPrint = useCallback(() => {
 *   // hooks 内に処理を閉じている
 *   onPrint();
 * }, [onPrint]);
 *
 * return (
 *   <div>
 *     <div>
 *       // other elements or components
 *       <button onClick={handleClickPrint}>印刷</button>
 *     </div>
 *
 *     // 印刷対象の element
 *     <div id={idForPrintSectionEl} className="hidden print:block" />
 *   </div>
 * )
 * ```
 */
export const useHandlePrint = (): Result => {
	/**
	 * print-block-ele id（印刷時はこの element に対象の element を append して印刷する）
	 */
	const idForPrintBlockEl = useMemo(() => "management-activities-options-print", []);

	/**
	 * print-section-ele id
	 */
	const [idForPrintSectionEl, setIdForSectionEl] = useState("");
	/**
	 * print-section-ele id を生成
	 */
	const onGenerateIdForPrintSectionEl = useCallback(({ prefix }: { prefix: string }) => {
		const id = `${prefix}-print-section`;
		setIdForSectionEl(id);
		return id;
	}, []);

	/**
	 * 印刷の実行
	 */
	const onPrint = useCallback(() => {
		const printSection = document.getElementById(idForPrintSectionEl);
		const printBlock = document.getElementById(idForPrintBlockEl);
		if (!printSection?.childNodes[0] || !printBlock) {
			notifyUserEventInfo({
				variant: "error",
				title: "印刷の表示に失敗しました",
				description: "再度お試しください",
			});
			return;
		}

		/**
		 * print-section の子要素を clone して print-block に append
		 */
		const clonedPrintSection = printSection.childNodes[0].cloneNode(true);
		printBlock.appendChild(clonedPrintSection);
		window.print();
	}, [idForPrintBlockEl, idForPrintSectionEl]);

	/**
	 * 副作用
	 */
	useEffect(() => {
		window.onafterprint = () => {
			const printBlock = document.getElementById(idForPrintBlockEl);
			if (printBlock && printBlock.childNodes[0]) {
				/**
				 * unmount 時に print-block の子要素を削除
				 */
				printBlock.removeChild(printBlock.childNodes[0]);
			}
		};
	}, [idForPrintBlockEl]);

	return {
		//
		idForPrintBlockEl,
		// print section id の生成
		onGenerateIdForPrintSectionEl,
		// プリントの実行
		onPrint,
	};
};
